import { Pipe, PipeTransform } from '@angular/core';

import { NotificationCode } from '../models/communication/app-enums.model';

@Pipe({
    name: 'notificationText',
    standalone: true
})
export class NotificationTextPipe implements PipeTransform {
    // Mapping of notification codes to localized text
    private static readonly notificationText: Record<NotificationCode, string> = {
        [NotificationCode.AuthenticationFailed]: $localize`Authentication failed!`,
        [NotificationCode.AuthenticationSuccess]: $localize`Successfully authenticated!`,
        [NotificationCode.CroupierChangeInvalidState]: $localize`Croupier change not allowed in this game state.`,
        [NotificationCode.DuplicateCard]: $localize`Same round, same card detected.`,
        [NotificationCode.FlagFailed]: $localize`Flag failed, round not marked.`,
        [NotificationCode.InvalidCard]: $localize`Invalid card or card not recognized.`,
        [NotificationCode.InvalidCroupierId]: $localize`Invalid croupier ID for this table!`,
        [NotificationCode.ScannerDisconnected]: $localize`Card scanner is disconnected!`,
        [NotificationCode.ScannerConnected]: $localize`Card scanner is connected and ready.`,
        [NotificationCode.InvalidDealing]: $localize`Invalid action - dealing in wrong state!`,
        [NotificationCode.CylinderNotReversed]: $localize`Cylinder was not reversed since the previous game.`,
        [NotificationCode.BallAndCylinderSameDirection]: $localize`Ball and cylinder are rotating in the same direction.`,
        [NotificationCode.TooFewBallRevolutions]: $localize`Ball did not complete enough revolutions for a valid spin.`,
        [NotificationCode.CylinderTooFast]: $localize`Cylinder is rotating above the maximum allowed speed.`,
        [NotificationCode.CylinderStopped]: $localize`Ball was thrown while the cylinder was not rotating.`,
        [NotificationCode.BallLost]: $localize`Wheel did not detect the ball after the throw. Spin again.`,
        [NotificationCode.ActionFailed]: $localize`Action failed!`,
        [NotificationCode.CutCardDrawn]: $localize`Cut card drawn.`,
        [NotificationCode.ConnectionClosed]: $localize`Connection to table lost. Attempting to reconnect…`,
        [NotificationCode.ExpectedSupervisor]: $localize`Expected worker with supervisor credentials.`,
        [NotificationCode.SecurityDoorOpened]: $localize`Table locked! Security door open:`,
        [NotificationCode.SecurityDoorClosed]: $localize`All security doors are closed!`,
        [NotificationCode.TableClosing]: $localize`Table is closing. Please finish current game.`,
        [NotificationCode.FreeHand]: $localize`Free hand mode enabled.`,
        [NotificationCode.GameChanging]: $localize`Game change in progress. Please wait…`,
        [NotificationCode.GameChangeCountdown]: $localize`Game will change soon. Please finish current game.`,
        [NotificationCode.CroupierLeaving]: $localize`Croupier is leaving the table. Please finish current game.`
    };

    transform(value?: NotificationCode, context?: unknown): string {
        // Check for custom message override
        if (context && typeof context === 'object' && 'message' in context) {
            const customMessage = (context as { message: unknown }).message;
            if (typeof customMessage === 'string' && customMessage.trim() !== '') {
                return customMessage;
            }
        }

        if (!value) {
            // eslint-disable-next-line
            console.warn('Invalid notification code');
            return '';
        }

        const baseText = NotificationTextPipe.codeToText(value);

        // Append context if present
        if (context && typeof context === 'object') {
            const contextText = NotificationTextPipe.formatContext(context);
            if (contextText) {
                return `${baseText} ${contextText}`;
            }
        }

        return baseText;
    }

    static codeToText(code: NotificationCode): string {
        const text = NotificationTextPipe.notificationText[code];

        if (!text) {
            // eslint-disable-next-line
            console.warn(`No text found for notification code: ${code}`);
            return '';
        }

        return text;
    }

    private static formatContext(context: object): string {
        const parts: string[] = [];

        for (const val of Object.values(context)) {
            if (Array.isArray(val) && val.length > 0) {
                parts.push(val.join(', '));
            } else if (typeof val === 'string' && val.trim() !== '') {
                parts.push(val);
            } else if (typeof val === 'number') {
                parts.push(String(val));
            }
        }

        return parts.length > 0 ? parts.join(' - ') : '';
    }
}
